import useSWR from 'swr';
import axios from '@/lib/axios';
import { useEffect, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import {
  UseAuthProps,
  RegisterProps,
  LoginProps,
  ForgotPasswordProps,
  ResetPasswordProps,
  ResendEmailVerificationProps,
} from '../types/types';
import { toast } from 'react-toastify';

export const useAuth = ({
  middleware,
  redirectIfAuthenticated = '',
  goBack,
}: UseAuthProps = {}) => {
  const router = useRouter();
  const params = useParams();
  const [loginError, setLoggingError] = useState(false);

  const {
    data: user,
    error,
    mutate,
    isLoading,
  } = useSWR<any>(
    '/api/user',
    () =>
      axios
        .get('/api/user')
        .then((res) => res.data)
        .catch((error) => {
          if (error.response.status !== 409) throw error;
          router.push('/verify-email');
        }),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
    }
  );

  const csrf = (): Promise<any> => axios.get('/sanctum/csrf-cookie');

  const register = async ({
    setErrors,
    ...props
  }: RegisterProps): Promise<void> => {
    await csrf();

    setErrors([]);

    axios
      .post('/register', props)
      .then(() => mutate())
      .catch((error) => {
        if (error.response.status !== 422) throw error;

        setErrors(error.response.data.errors);
      });
  };

  const login = async ({
    setErrors,
    setStatus,
    ...props
  }: LoginProps): Promise<void> => {
    await csrf();

    setErrors([]);
    if (setStatus) setStatus(null);

    try {
      const response = await axios.post('/api/v2/auth/web/login', props);

      if (
        response.status === 204 ||
        response.status === 200 ||
        response?.data?.result
      ) {
        await mutate(); // Re-fetch user data
        if (response?.data?.message) {
          toast.success(response.data.message);
        } else {
          toast.success('Logged in successfully!');
        }

        // <-- FIX: Perform redirect immediately instead of waiting for useEffect
        if (redirectIfAuthenticated) {
          router.push(redirectIfAuthenticated);
        }
      } else {
        toast.error(response?.data?.message ?? 'Login failed.');
        setErrors(response?.data);
      }
    } catch (error: any) {
      if (error.response && error.response.status === 422) {
        // Set form errors for validation issues
        setErrors(error.response.data.errors);
      } else if (error.response && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('An unexpected error occurred. Please try again.');
      }
      // --- FIX: Re-throw error so the component knows the login failed ---
      throw error;
    }
  };

  const LoginWithOTP = async ({ ...props }) => {
    await csrf(); // Good practice to include csrf for state-changing requests

    try {
      const res = await axios.post('/api/v2/verify-login-otp', props);
      console.log(res);

      if (res.data.result) {
        await mutate(); // Re-fetch user data
        toast.success(res.data.message || 'Successfully logged in!');

        // <-- FIX: Perform redirect immediately instead of waiting for useEffect
        if (redirectIfAuthenticated) {
          router.push(redirectIfAuthenticated);
        }
      } else {
        toast.error(res.data.message || 'An error occurred.');
        throw new Error(res.data.message || 'Verification failed');
      }
    } catch (error: any) {
      if (error.response?.status !== 422) {
        toast.error(
          error.response?.data?.message || 'Invalid OTP or an error occurred.'
        );
      }
      throw error;
    }

    // axios
    //   .post('/api/v2/verify-login-otp', props)
    //   .then((res) => {
    //     mutate();
    //     if (res.data.result) {
    //       toast.success(res.data.message);
    //     } else {
    //       toast.error(res.data.message);
    //     }
    //   })
    //   .catch((error) => {
    //     if (error.response.status !== 422) throw error;
    //     return error;
    //   });
  };

  const forgotPassword = async ({
    setErrors,
    setStatus,
    email,
  }: ForgotPasswordProps): Promise<void> => {
    await csrf();

    setErrors([]);
    if (setStatus) setStatus(null);

    axios
      .post('/api/v2/send-reset-otp', { email })
      .then((response) => {
        if (setStatus) {
          setStatus(response.data.status); // Check for setStatus before invoking
        }
      })
      .catch((error) => {
        if (error.response.status !== 422) throw error;

        setErrors(error.response.data.errors);
      });
  };

  const resetPassword = async ({
    setErrors,
    setStatus,
    ...props
  }: ResetPasswordProps): Promise<void> => {
    await csrf();

    setErrors([]);
    if (setStatus) setStatus(null);

    axios
      .post('/reset-password', { token: params.token, ...props })
      .then((response) =>
        router.push('/login?reset=' + btoa(response.data.status))
      )
      .catch((error) => {
        if (error.response.status !== 422) throw error;

        setErrors(error.response.data.errors);
      });
  };

  const resendEmailVerification = ({
    setStatus,
  }: ResendEmailVerificationProps): void => {
    axios
      .post('/email/verification-notification')
      .then((response) => setStatus(response.data.status));
  };

  const logout = async (): Promise<void> => {
    if (!error) {
      await axios.post('/api/v2/auth/web/logout').then(() => mutate());
    }

    window.location.pathname = '/login';
  };

  useEffect(() => {
    if (user && goBack) {
      return router.back();
    }
    if (middleware === 'guest' && redirectIfAuthenticated && user) {
      router.push(redirectIfAuthenticated);
    }

    if (middleware === 'auth' && error) logout();
  }, [user, error, middleware, redirectIfAuthenticated, router]);

  return {
    user,
    register,
    login,
    forgotPassword,
    resetPassword,
    resendEmailVerification,
    logout,
    mutate,
    LoginWithOTP,
    loginError,
    isLoading,
  };
};

// import useSWR from 'swr';
// import axios from '@/lib/axios';
// import { useEffect, useState } from 'react';
// import { useParams, useRouter } from 'next/navigation';
// import {
//   UseAuthProps,
//   RegisterProps,
//   LoginProps,
//   ForgotPasswordProps,
//   ResetPasswordProps,
//   ResendEmailVerificationProps,
// } from '../types/types';
// import { toast } from 'react-toastify';

// export const useAuth = ({
//   middleware,
//   redirectIfAuthenticated = '',
//   goBack = false,
// }: UseAuthProps = {}) => {
//   const router = useRouter();
//   const params = useParams();
//   const [loginError, setLoggingError] = useState(false);

//   const {
//     data: user,
//     error,
//     mutate,
//     isLoading,
//   } = useSWR<any>(
//     '/api/user',
//     () =>
//       axios
//         .get('/api/user')
//         .then((res) => res.data)
//         .catch((error) => {
//           if (error.response.status !== 409) throw error;
//           router.push('/verify-email');
//         }),
//     {
//       revalidateIfStale: false,
//       revalidateOnFocus: false,
//       revalidateOnReconnect: false,
//     }
//   );

//   const csrf = (): Promise<any> => axios.get('/sanctum/csrf-cookie');

//   const register = async ({
//     setErrors,
//     ...props
//   }: RegisterProps): Promise<void> => {
//     await csrf();

//     setErrors([]);

//     axios
//       .post('/register', props)
//       .then(() => mutate())
//       .catch((error) => {
//         if (error.response.status !== 422) throw error;

//         setErrors(error.response.data.errors);
//       });
//   };

//   const login = async ({
//     setErrors,
//     setStatus,
//     ...props
//   }: LoginProps): Promise<void> => {
//     await csrf();

//     setErrors([]);
//     if (setStatus) setStatus(null);

//     try {
//       const response = await axios.post('/api/v2/auth/web/login', props);

//       if (
//         response.status === 204 ||
//         response.status === 200 ||
//         response?.data?.result
//       ) {
//         await mutate();
//         if (response?.data?.message) {
//           toast.success(response.data.message);
//         } else {
//           toast.success('Logged in successfully!');
//         }
//       } else {
//         toast.error(response?.data?.message ?? 'Login failed.');
//         setErrors(response?.data);
//       }
//     } catch (error: any) {
//       if (error.response && error.response.data.message) {
//         setErrors(error.response.data.message);
//         toast.error(error.response.data.message);
//       } else {
//         toast.error('An unexpected error occurred. Please try again.');
//       }
//     }
//   };

//   const LoginWithOTP = async ({ ...props }) => {
//     axios
//       .post('/api/v2/verify-login-otp', props)
//       .then((res) => {
//         mutate();
//         if (res.data.result) {
//           toast.success(res.data.message);
//           // if (redirectIfAuthenticated === 'goBack') {
//           //   router.back();
//           // } else if (redirectIfAuthenticated) {
//           //   router.push(redirectIfAuthenticated);
//           // }
//         } else {
//           toast.error(res.data.message);
//         }
//       })
//       .catch((error) => {
//         if (error.response.status !== 422) throw error;
//         return error;
//       });
//   };

//   const forgotPassword = async ({
//     setErrors,
//     setStatus,
//     email,
//   }: ForgotPasswordProps): Promise<void> => {
//     await csrf();

//     setErrors([]);
//     if (setStatus) setStatus(null);

//     axios
//       .post('/api/v2/send-reset-otp', { email })
//       .then((response) => {
//         if (setStatus) {
//           setStatus(response.data.status); // Check for setStatus before invoking
//         }
//       })
//       .catch((error) => {
//         if (error.response.status !== 422) throw error;

//         setErrors(error.response.data.errors);
//       });
//   };

//   const resetPassword = async ({
//     setErrors,
//     setStatus,
//     ...props
//   }: ResetPasswordProps): Promise<void> => {
//     await csrf();

//     setErrors([]);
//     if (setStatus) setStatus(null);

//     axios
//       .post('/reset-password', { token: params.token, ...props })
//       .then((response) =>
//         router.push('/login?reset=' + btoa(response.data.status))
//       )
//       .catch((error) => {
//         if (error.response.status !== 422) throw error;

//         setErrors(error.response.data.errors);
//       });
//   };

//   const resendEmailVerification = ({
//     setStatus,
//   }: ResendEmailVerificationProps): void => {
//     axios
//       .post('/email/verification-notification')
//       .then((response) => setStatus(response.data.status));
//   };

//   const logout = async (): Promise<void> => {
//     if (!error) {
//       await axios.post('/api/v2/auth/web/logout').then(() => mutate());
//     }

//     window.location.pathname = '/login';
//   };

//   useEffect(() => {
//     if (middleware === 'guest' && redirectIfAuthenticated && user) {
//       if (goBack) {
//         router.back();
//       } else {
//         router.push(redirectIfAuthenticated);
//       }
//     }

//     if (middleware === 'auth' && error) logout();
//   }, [user, error, middleware, redirectIfAuthenticated, router]);

//   return {
//     user,
//     register,
//     login,
//     forgotPassword,
//     resetPassword,
//     resendEmailVerification,
//     logout,
//     mutate,
//     LoginWithOTP,
//     loginError,
//     isLoading,
//   };
// };
